/**
 * Exact PNG decoder for the browser platform (also runs in Node >= 18).
 *
 * Why not `createImageBitmap` + `getImageData`? A 2D canvas stores premultiplied pixels, so a
 * canvas round trip changes the RGB of semi-transparent pixels. BitmapData pixels drive game logic
 * (terrain collision masks, `getPixel32`, `threshold`, `hitTest`), so the browser must see exactly
 * the same straight RGBA as the headless runtime (NodePlatform decodes with pngjs) — otherwise
 * browser and headless server simulations could diverge. Inflate uses the native
 * `DecompressionStream('deflate')` (zlib format, as in PNG IDAT).
 *
 * Supported: all colour types (0 grey, 2 RGB, 3 palette, 4 grey+alpha, 6 RGBA), bit depths
 * 1/2/4/8/16, tRNS (palette alpha and grey/RGB colour key), Adam7 interlacing, all five filters.
 * Output is byte-identical to pngjs (what NodePlatform uses): 16-bit and sub-byte samples are
 * rescaled with rounding (round(v * 255 / max)), colour-keyed (tRNS) grey/RGB pixels become
 * transparent black. Gamma/ICC chunks are ignored (the extracted assets carry none).
 */
import type { DecodedImage } from './types';

export type InflateFn = (data: Uint8Array) => Promise<Uint8Array>;

const SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

export function isPng(bytes: Uint8Array): boolean {
  if (bytes.length < 8) return false;
  for (let i = 0; i < 8; i++) if (bytes[i] !== SIGNATURE[i]) return false;
  return true;
}

/** zlib inflate through the platform's native DecompressionStream. */
export async function inflateZlib(data: Uint8Array): Promise<Uint8Array> {
  const DS: any = (globalThis as any).DecompressionStream;
  if (typeof DS !== 'function') throw new Error('DecompressionStream is not available');
  const stream = new Blob([data as unknown as BlobPart]).stream().pipeThrough(new DS('deflate'));
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

interface Header {
  width: number;
  height: number;
  bitDepth: number;
  colorType: number;
  interlace: number;
}

const CHANNELS: Record<number, number> = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 };
const LITTLE_ENDIAN = new Uint8Array(new Uint32Array([1]).buffer)[0] === 1;

/** Parse PNG chunks: header, palette, transparency, concatenated IDAT. */
export function parsePngChunks(bytes: Uint8Array): { header: Header; palette: Uint8Array | null; trns: Uint8Array | null; idat: Uint8Array } {
  if (!isPng(bytes)) throw new Error('Not a PNG file');
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = 8;
  let header: Header | null = null;
  let palette: Uint8Array | null = null;
  let trns: Uint8Array | null = null;
  const idats: Uint8Array[] = [];
  let idatLen = 0;
  while (pos + 8 <= bytes.length) {
    const len = dv.getUint32(pos);
    const type = String.fromCharCode(bytes[pos + 4], bytes[pos + 5], bytes[pos + 6], bytes[pos + 7]);
    const start = pos + 8;
    const end = start + len;
    if (end > bytes.length) throw new Error(`Truncated PNG chunk ${type}`);
    const data = bytes.subarray(start, end);
    if (type === 'IHDR') {
      header = {
        width: dv.getUint32(start),
        height: dv.getUint32(start + 4),
        bitDepth: bytes[start + 8],
        colorType: bytes[start + 9],
        interlace: bytes[start + 12],
      };
      if (bytes[start + 10] !== 0 || bytes[start + 11] !== 0) throw new Error('Unsupported PNG compression/filter method');
    } else if (type === 'PLTE') palette = data;
    else if (type === 'tRNS') trns = data;
    else if (type === 'IDAT') {
      idats.push(data);
      idatLen += data.length;
    } else if (type === 'IEND') break;
    pos = end + 4; // skip CRC (not verified: assets come from our own pipeline)
  }
  if (!header) throw new Error('PNG without IHDR');
  if (!(header.colorType in CHANNELS)) throw new Error(`Unsupported PNG colour type ${header.colorType}`);
  if (![1, 2, 4, 8, 16].includes(header.bitDepth)) throw new Error(`Unsupported PNG bit depth ${header.bitDepth}`);
  let idat: Uint8Array;
  if (idats.length === 1) idat = idats[0];
  else {
    idat = new Uint8Array(idatLen);
    let o = 0;
    for (const c of idats) {
      idat.set(c, o);
      o += c.length;
    }
  }
  return { header, palette, trns, idat };
}

function paeth(a: number, b: number, c: number): number {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  if (pa <= pb && pa <= pc) return a;
  if (pb <= pc) return b;
  return c;
}

/** Undo PNG scanline filters in place; returns the offset after the pass. */
function unfilterPass(raw: Uint8Array, offset: number, out: Uint8Array, rowBytes: number, rows: number, bpp: number): number {
  let prev = -1; // start of previous unfiltered row in `out`
  let o = 0;
  for (let y = 0; y < rows; y++) {
    const ft = raw[offset++];
    const rowStart = o;
    switch (ft) {
      case 0:
        out.set(raw.subarray(offset, offset + rowBytes), o);
        break;
      case 1:
        for (let i = 0; i < rowBytes; i++) out[o + i] = (raw[offset + i] + (i >= bpp ? out[o + i - bpp] : 0)) & 255;
        break;
      case 2:
        for (let i = 0; i < rowBytes; i++) out[o + i] = (raw[offset + i] + (prev >= 0 ? out[prev + i] : 0)) & 255;
        break;
      case 3:
        for (let i = 0; i < rowBytes; i++) {
          const left = i >= bpp ? out[o + i - bpp] : 0;
          const up = prev >= 0 ? out[prev + i] : 0;
          out[o + i] = (raw[offset + i] + ((left + up) >> 1)) & 255;
        }
        break;
      case 4:
        for (let i = 0; i < rowBytes; i++) {
          const left = i >= bpp ? out[o + i - bpp] : 0;
          const up = prev >= 0 ? out[prev + i] : 0;
          const ul = prev >= 0 && i >= bpp ? out[prev + i - bpp] : 0;
          out[o + i] = (raw[offset + i] + paeth(left, up, ul)) & 255;
        }
        break;
      default:
        throw new Error(`Bad PNG filter type ${ft}`);
    }
    offset += rowBytes;
    prev = rowStart;
    o += rowBytes;
  }
  return offset;
}

/** Convert unfiltered scanlines of a (sub)image to RGBA8 pixels written through `put`. */
function expandRows(
  data: Uint8Array,
  w: number,
  h: number,
  rowBytes: number,
  hd: Header,
  palette: Uint8Array | null,
  trns: Uint8Array | null,
  put: (x: number, y: number, r: number, g: number, b: number, a: number) => void,
): void {
  const { bitDepth, colorType } = hd;
  const maxv = (1 << Math.min(bitDepth, 8)) - 1;
  const scale = bitDepth < 8 ? 255 / maxv : 1;
  // Colour key (tRNS for grey / RGB) in sample units of the image's bit depth.
  let keyG = -1;
  let keyR = -1;
  let keyGr = -1;
  let keyB = -1;
  if (trns && colorType === 0 && trns.length >= 2) keyG = (trns[0] << 8) | trns[1];
  if (trns && colorType === 2 && trns.length >= 6) {
    keyR = (trns[0] << 8) | trns[1];
    keyGr = (trns[2] << 8) | trns[3];
    keyB = (trns[4] << 8) | trns[5];
  }
  for (let y = 0; y < h; y++) {
    const row = y * rowBytes;
    if (bitDepth < 8) {
      // Packed samples (grey or palette, 1 channel).
      const perByte = 8 / bitDepth;
      const mask = maxv;
      for (let x = 0; x < w; x++) {
        const byte = data[row + Math.floor(x / perByte)];
        const shift = 8 - bitDepth * ((x % perByte) + 1);
        const v = (byte >> shift) & mask;
        if (colorType === 3) {
          if (!palette || v * 3 + 2 >= palette.length) put(x, y, 0, 0, 0, 255);
          else put(x, y, palette[v * 3], palette[v * 3 + 1], palette[v * 3 + 2], trns && v < trns.length ? trns[v] : 255);
        } else if (v === keyG) put(x, y, 0, 0, 0, 0);
        else {
          const g = Math.floor(v * scale + 0.5);
          put(x, y, g, g, g, 255);
        }
      }
      continue;
    }
    const bps = bitDepth === 16 ? 2 : 1;
    const ch = CHANNELS[colorType];
    for (let x = 0; x < w; x++) {
      const p = row + x * ch * bps;
      // Full sample value (colour keys compare these) and its 8-bit rescale (pngjs rounding).
      const full = (i: number) => (bps === 2 ? (data[p + i * 2] << 8) | data[p + i * 2 + 1] : data[p + i]);
      const s = (i: number) => (bps === 2 ? Math.floor((full(i) * 255) / 65535 + 0.5) : data[p + i]);
      switch (colorType) {
        case 0: {
          if (full(0) === keyG) put(x, y, 0, 0, 0, 0);
          else {
            const g = s(0);
            put(x, y, g, g, g, 255);
          }
          break;
        }
        case 2: {
          if (keyR >= 0 && full(0) === keyR && full(1) === keyGr && full(2) === keyB) put(x, y, 0, 0, 0, 0);
          else put(x, y, s(0), s(1), s(2), 255);
          break;
        }
        case 3: {
          const v = data[p];
          if (!palette || v * 3 + 2 >= palette.length) put(x, y, 0, 0, 0, 255);
          else put(x, y, palette[v * 3], palette[v * 3 + 1], palette[v * 3 + 2], trns && v < trns.length ? trns[v] : 255);
          break;
        }
        case 4: {
          const g = s(0);
          put(x, y, g, g, g, s(1));
          break;
        }
        case 6:
          put(x, y, s(0), s(1), s(2), s(3));
          break;
      }
    }
  }
}

// Adam7 passes: [xStart, yStart, xStep, yStep]
const ADAM7: Array<[number, number, number, number]> = [
  [0, 0, 8, 8],
  [4, 0, 8, 8],
  [0, 4, 4, 8],
  [2, 0, 4, 4],
  [0, 2, 2, 4],
  [1, 0, 2, 2],
  [0, 1, 1, 2],
];

/** Decode inflated PNG image data into straight RGBA8. */
export function decodePngPixels(hd: Header, palette: Uint8Array | null, trns: Uint8Array | null, raw: Uint8Array): DecodedImage {
  const { width, height, bitDepth, colorType } = hd;
  const ch = CHANNELS[colorType];
  const bitsPP = ch * bitDepth;
  const bpp = Math.max(1, bitsPP >> 3);
  const out = new Uint8ClampedArray(width * height * 4);
  if (hd.interlace === 0) {
    const rowBytes = Math.ceil((width * bitsPP) / 8);
    const buf = new Uint8Array(rowBytes * height);
    unfilterPass(raw, 0, buf, rowBytes, height, bpp);
    // Fast paths for the formats the extracted assets use (8-bit palette / RGB / RGBA).
    if (colorType === 6 && bitDepth === 8) out.set(buf);
    else if (colorType === 3 && bitDepth === 8 && LITTLE_ENDIAN) {
      const lut = new Uint32Array(256).fill(0xff000000);
      if (palette) {
        for (let i = 0; i < 256 && i * 3 + 2 < palette.length; i++) {
          const a = trns && i < trns.length ? trns[i] : 255;
          lut[i] = ((a << 24) | (palette[i * 3 + 2] << 16) | (palette[i * 3 + 1] << 8) | palette[i * 3]) >>> 0;
        }
      }
      const o32 = new Uint32Array(out.buffer, out.byteOffset, width * height);
      for (let y = 0, k = 0; y < height; y++) {
        const row = y * rowBytes;
        for (let x = 0; x < width; x++) o32[k++] = lut[buf[row + x]];
      }
    } else if (colorType === 2 && bitDepth === 8 && !trns) {
      for (let y = 0, k = 0; y < height; y++) {
        const row = y * rowBytes;
        for (let x = 0, p = row; x < width; x++, p += 3) {
          out[k++] = buf[p];
          out[k++] = buf[p + 1];
          out[k++] = buf[p + 2];
          out[k++] = 255;
        }
      }
    } else {
      expandRows(buf, width, height, rowBytes, hd, palette, trns, (x, y, r, g, b, a) => {
        const i = (y * width + x) * 4;
        out[i] = r;
        out[i + 1] = g;
        out[i + 2] = b;
        out[i + 3] = a;
      });
    }
    return { width, height, data: out };
  }
  let offset = 0;
  for (const [x0, y0, dx, dy] of ADAM7) {
    const pw = Math.ceil((width - x0) / dx);
    const ph = Math.ceil((height - y0) / dy);
    if (pw <= 0 || ph <= 0) continue;
    const rowBytes = Math.ceil((pw * bitsPP) / 8);
    const buf = new Uint8Array(rowBytes * ph);
    offset = unfilterPass(raw, offset, buf, rowBytes, ph, bpp);
    expandRows(buf, pw, ph, rowBytes, hd, palette, trns, (x, y, r, g, b, a) => {
      const i = ((y0 + y * dy) * width + (x0 + x * dx)) * 4;
      out[i] = r;
      out[i + 1] = g;
      out[i + 2] = b;
      out[i + 3] = a;
    });
  }
  return { width, height, data: out };
}

/** Decode a PNG file to straight (non-premultiplied) RGBA8. */
export async function decodePng(bytes: Uint8Array, inflate: InflateFn = inflateZlib): Promise<DecodedImage> {
  const { header, palette, trns, idat } = parsePngChunks(bytes);
  const raw = await inflate(idat);
  return decodePngPixels(header, palette, trns, raw);
}
